import React, { useEffect } from 'react'
import { Modal, Form, Select, InputNumber, DatePicker, Input, Row, Col, Typography, theme } from 'antd'
import dayjs from 'dayjs'
import { usePaymentOptions } from '@/hooks/usePayments'
import { parseDataPura } from '@/utils/dateUtils'
import type { WorkOrderPayment, WorkOrderPaymentCreate } from '@/types/payments'

const { Text } = Typography

const TIPO_LABELS: Record<string, string> = { acconto: 'Acconto', rata: 'Rata', saldo: 'Saldo' }
const STATO_LABELS: Record<string, string> = { previsto: 'Previsto', incassato: 'Incassato' }

const formatEuro = (val: number): string => `${val.toFixed(2)} €`

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (data: WorkOrderPaymentCreate) => void
  loading: boolean
  payment?: WorkOrderPayment
  daPianificare?: number | null
}

const PagamentoModal: React.FC<Props> = ({ open, onClose, onConfirm, loading, payment, daPianificare }) => {
  const [form] = Form.useForm()
  const { token } = theme.useToken()
  const isEditing = !!payment
  const { data: options } = usePaymentOptions()

  const stato = Form.useWatch('stato', form) as string | undefined
  const importoWatch = Form.useWatch('importo', form) as number | undefined

  const showExceedsWarning =
    !isEditing &&
    daPianificare != null &&
    daPianificare > 0 &&
    importoWatch != null &&
    importoWatch > daPianificare

  useEffect(() => {
    if (!open) {
      form.resetFields()
      return
    }
    form.resetFields()
    if (payment) {
      form.setFieldsValue({
        tipo: payment.tipo,
        stato: payment.stato,
        importo: Number(payment.importo),
        forma_pagamento: payment.forma_pagamento ?? undefined,
        data_prevista: parseDataPura(payment.data_prevista),
        data_pagamento: parseDataPura(payment.data_pagamento),
        numero_rata: payment.numero_rata ?? undefined,
        totale_rate: payment.totale_rate ?? undefined,
        pagatore: payment.pagatore ?? undefined,
        note: payment.note ?? undefined,
      })
    } else if (daPianificare != null && daPianificare > 0) {
      form.setFieldValue('importo', parseFloat(daPianificare.toFixed(2)))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleOk = async () => {
    try {
      const v = await form.validateFields()
      // Per stato='previsto', data_pagamento deve essere null (valida_coerenza rifiuterebbe)
      const dataPagamento = stato === 'previsto'
        ? null
        : v.data_pagamento ? (v.data_pagamento as dayjs.Dayjs).format('YYYY-MM-DD') : null

      onConfirm({
        tipo: v.tipo,
        stato: v.stato,
        importo: v.importo,
        forma_pagamento: v.forma_pagamento ?? null,
        data_prevista: v.data_prevista ? (v.data_prevista as dayjs.Dayjs).format('YYYY-MM-DD') : null,
        data_pagamento: dataPagamento,
        numero_rata: v.numero_rata ?? null,
        totale_rate: v.totale_rate ?? null,
        pagatore: v.pagatore ?? null,
        note: v.note ?? null,
      })
    } catch {
      // errori di validazione form mostrati inline
    }
  }

  const tipiOpt = (options?.tipi_pagamento ?? []).map(v => ({ value: v, label: TIPO_LABELS[v] ?? v }))
  const statiOpt = (options?.stati_pagamento ?? []).map(v => ({ value: v, label: STATO_LABELS[v] ?? v }))
  const formeOpt = (options?.forme_pagamento ?? []).map(v => ({ value: v, label: v }))
  const pagatoriOpt = (options?.pagatori ?? []).map(v => ({ value: v, label: v }))

  return (
    <Modal
      open={open}
      title={isEditing ? 'Modifica pagamento' : 'Registra pagamento'}
      okText={isEditing ? 'Salva' : 'Registra'}
      cancelText="Annulla"
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={loading}
      destroyOnClose
      width={480}
    >
      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={(changed) => {
          if ('stato' in changed && changed.stato === 'previsto') {
            form.setFieldValue('data_pagamento', undefined)
          }
        }}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="tipo" label="Tipo" rules={[{ required: true, message: 'Obbligatorio' }]}>
              <Select options={tipiOpt} placeholder="Seleziona..." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="stato" label="Stato" rules={[{ required: true, message: 'Obbligatorio' }]}>
              <Select options={statiOpt} placeholder="Seleziona..." />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="importo"
              label="Importo (€)"
              rules={[
                { required: true, message: 'Obbligatorio' },
                { type: 'number', min: 0.01, message: 'Deve essere > 0' },
              ]}
            >
              <InputNumber min={0.01} precision={2} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="forma_pagamento" label="Forma di pagamento">
              <Select options={formeOpt} placeholder="Seleziona..." allowClear />
            </Form.Item>
          </Col>
        </Row>

        {!isEditing && (
          <div style={{ marginTop: -8, marginBottom: 12, fontSize: 12 }}>
            {daPianificare == null ? (
              <Text type="secondary">Scheda non quotata — nessun importo dovuto</Text>
            ) : daPianificare <= 0 ? (
              <Text type="secondary">Dovuto già registrato per intero</Text>
            ) : (
              <Text style={{ color: token.colorWarning }}>
                Da registrare: {formatEuro(daPianificare)}
              </Text>
            )}
          </div>
        )}

        {showExceedsWarning && (
          <div style={{ marginTop: -8, marginBottom: 12, fontSize: 12 }}>
            <Text style={{ color: token.colorWarning }}>
              L'importo supera il da registrare ({formatEuro(daPianificare!)})
            </Text>
          </div>
        )}

        <Row gutter={12}>
          <Col span={stato === 'incassato' ? 12 : 24}>
            <Form.Item
              name="data_prevista"
              label="Data prevista"
              rules={stato === 'previsto' ? [{ required: true, message: 'Obbligatoria per pagamento previsto' }] : []}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          {stato === 'incassato' && (
            <Col span={12}>
              <Form.Item
                name="data_pagamento"
                label="Data pagamento"
                rules={[{ required: true, message: 'Obbligatoria per pagamento incassato' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Form.Item name="pagatore" label="Pagatore">
          <Select options={pagatoriOpt} placeholder="Seleziona..." allowClear />
        </Form.Item>

        <Form.Item name="note" label="Note" style={{ marginBottom: 0 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default PagamentoModal
